/**
 * QuotationStatusActions — status-aware action buttons for quotations.
 *
 * Shows only the valid transitions from the current status:
 *   draft    -> [Enviar]
 *   sent     -> [Aceptar] [Rechazar]
 *   accepted -> (no actions)
 *   rejected -> (no actions)
 *
 * Status badges: draft=gray, sent=blue, accepted=green, rejected=red.
 */

import type { QuotationStatus } from '@1klick/types'
import { ALLOWED_TRANSITIONS } from '@1klick/types'

interface QuotationStatusActionsProps {
  status: QuotationStatus
  onAdvance: (newStatus: QuotationStatus) => void
  isAdvancing: boolean
}

const STATUS_LABELS: Record<QuotationStatus, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
}

const STATUS_BADGE_CLASSES: Record<QuotationStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const ACTION_BUTTON_CONFIG: Record<
  QuotationStatus,
  { label: string; className: string } | undefined
> = {
  sent: {
    label: 'Enviar',
    className: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  accepted: {
    label: 'Aceptar',
    className: 'bg-green-600 hover:bg-green-700 text-white',
  },
  rejected: {
    label: 'Rechazar',
    className: 'bg-red-600 hover:bg-red-700 text-white',
  },
  draft: undefined,
}

/**
 * Status badge component — displays the current status with color coding.
 */
export function QuotationStatusBadge({ status }: { status: QuotationStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

/**
 * Action buttons for transitioning quotation status.
 * Only renders buttons for valid next states.
 */
export function QuotationStatusActions({
  status,
  onAdvance,
  isAdvancing,
}: QuotationStatusActionsProps) {
  const allowedTargets = ALLOWED_TRANSITIONS[status]

  if (!allowedTargets || allowedTargets.length === 0) {
    return null
  }

  return (
    <div className="flex gap-2">
      {allowedTargets.map((targetStatus) => {
        const config = ACTION_BUTTON_CONFIG[targetStatus]
        if (!config) return null
        return (
          <button
            key={targetStatus}
            onClick={() => onAdvance(targetStatus)}
            disabled={isAdvancing}
            className={`rounded-md px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${config.className}`}
          >
            {isAdvancing ? 'Procesando...' : config.label}
          </button>
        )
      })}
    </div>
  )
}
